import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './sticky-detail-header.module.css';

const STICKY_BAR_HEIGHT = '64px';

interface StickyDetailHeaderProps {
    imageUrl?: string;
    title?: string;
    subtitle?: string;
    renderActions?: () => ReactNode;
    targetRef: RefObject<HTMLElement>;
    onStickyBarHeightChange?: (height: string) => void;
}

export function StickyDetailHeader({
    imageUrl,
    title,
    subtitle,
    renderActions,
    targetRef,
    onStickyBarHeightChange,
}: StickyDetailHeaderProps) {
    const [isVisible, setIsVisible] = useState(false);
    const isVisibleRef = useRef(false);

    useEffect(() => {
        const target = targetRef.current;
        if (!target) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const nextVisible = !entry.isIntersecting;

                if (nextVisible === isVisibleRef.current) return;

                isVisibleRef.current = nextVisible;
                setIsVisible(nextVisible);
            },
            {
                threshold: 0,
                rootMargin: '-72px 0px 0px 0px',
            }
        );

        observer.observe(target);

        requestAnimationFrame(() => {
            const [entry] = observer.takeRecords();
            if (!entry) return;

            const nextVisible = !entry.isIntersecting;
            isVisibleRef.current = nextVisible;
            setIsVisible(nextVisible);
        });

        return () => {
            observer.disconnect();
            isVisibleRef.current = false;
            onStickyBarHeightChange?.('0px');
        };
    }, [onStickyBarHeightChange, targetRef, title]);

    useEffect(() => {
        onStickyBarHeightChange?.(isVisible ? STICKY_BAR_HEIGHT : '0px');
    }, [isVisible, onStickyBarHeightChange]);

    return (
        <div
            className={classNames(styles['sticky-detail-header'], {
                [styles['sticky-detail-header--visible']]: isVisible,
            })}
            aria-hidden={!isVisible}
        >
            {imageUrl && (
                <div className={styles['sticky-detail-header__image-container']}>
                    <img src={imageUrl} alt="" className={styles['sticky-detail-header__image']} draggable={false} />
                </div>
            )}
            <div className={styles['sticky-detail-header__text']}>
                {title && <p className={styles['sticky-detail-header__title']}>{title}</p>}
                {subtitle && <p className={styles['sticky-detail-header__subtitle']}>{subtitle}</p>}
            </div>
            {isVisible && renderActions && (
                <div className={styles['sticky-detail-header__actions']}>{renderActions()}</div>
            )}
        </div>
    );
}
